import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&>svg]:pointer-events-none [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        // Premium convex primary - raised 3D effect with gradient depth
        default:
          "bg-gradient-to-b from-leaf-400 via-leaf-500 to-leaf-600 text-white border border-white/20 shadow-[0_1px_0_0_rgba(255,255,255,0.25)_inset,0_-1px_0_0_rgba(0,0,0,0.1)_inset,0_4px_12px_-2px_rgba(34,197,94,0.35),0_2px_4px_-1px_rgba(0,0,0,0.15)] hover:from-leaf-300 hover:via-leaf-400 hover:to-leaf-500 hover:shadow-[0_1px_0_0_rgba(255,255,255,0.35)_inset,0_-1px_0_0_rgba(0,0,0,0.15)_inset,0_8px_20px_-4px_rgba(34,197,94,0.45),0_4px_8px_-2px_rgba(0,0,0,0.2)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_-1px_0_0_rgba(0,0,0,0.1)_inset,0_2px_6px_-1px_rgba(34,197,94,0.3)]",
        // Premium convex destructive - bold red with depth
        destructive:
          "bg-gradient-to-b from-leaf-400 via-leaf-500 to-leaf-600 text-white border border-white/20 shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_-1px_0_0_rgba(0,0,0,0.15)_inset,0_4px_12px_-2px_rgba(234,88,12,0.35),0_2px_4px_-1px_rgba(0,0,0,0.15)] hover:from-leaf-300 hover:via-leaf-400 hover:to-leaf-500 hover:shadow-[0_1px_0_0_rgba(255,255,255,0.3)_inset,0_-1px_0_0_rgba(0,0,0,0.2)_inset,0_8px_20px_-4px_rgba(234,88,12,0.45),0_4px_8px_-2px_rgba(0,0,0,0.2)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_-1px_0_0_rgba(0,0,0,0.1)_inset,0_2px_6px_-1px_rgba(234,88,12,0.3)]",
        // Premium convex outline - subtle raised surface
        outline:
          "bg-gradient-to-b from-card to-card/95 text-foreground border border-border shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_-1px_0_0_rgba(0,0,0,0.05)_inset,0_2px_4px_-1px_rgba(0,0,0,0.1)] hover:from-card/95 hover:to-card/90 hover:border-border/80 hover:shadow-[0_1px_0_0_rgba(255,255,255,0.08)_inset,0_-1px_0_0_rgba(0,0,0,0.08)_inset,0_4px_8px_-2px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset,0_-1px_0_0_rgba(0,0,0,0.03)_inset,0_1px_2px_rgba(0,0,0,0.08)]",
        // Ghost - minimal convex with subtle lift
        ghost:
          "text-muted-foreground bg-gradient-to-b from-transparent to-black/[0.02] shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset] hover:bg-accent/50 hover:text-foreground hover:shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_2px_4px_-1px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 active:translate-y-0",
        link: "text-leaf-500 underline-offset-4 hover:underline",
        // Premium convex secondary - glass with raised effect
        secondary:
          "glass bg-gradient-to-b from-white/[0.08] to-white/[0.04] border border-white/15 text-foreground shadow-[0_1px_0_0_rgba(255,255,255,0.1)_inset,0_-1px_0_0_rgba(0,0,0,0.05)_inset,0_2px_8px_-2px_rgba(0,0,0,0.15)] hover:from-white/[0.12] hover:to-white/[0.06] hover:border-white/25 hover:shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_-1px_0_0_rgba(0,0,0,0.08)_inset,0_4px_12px_-2px_rgba(0,0,0,0.2)] hover:-translate-y-0.5 active:translate-y-0",
        // Premium convex glass - strong glass with depth
        glass:
          "glass-strong bg-gradient-to-b from-white/[0.12] to-white/[0.06] border border-white/20 text-foreground shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset,0_-1px_0_0_rgba(0,0,0,0.08)_inset,0_4px_16px_-4px_rgba(0,0,0,0.2)] hover:from-white/[0.18] hover:to-white/[0.1] hover:border-white/30 hover:shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_-1px_0_0_rgba(0,0,0,0.1)_inset,0_8px_24px_-4px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 active:translate-y-0",
      },
      size: {
        default: "min-h-[44px] px-5 py-2.5",
        sm: "min-h-[38px] rounded-lg px-4 py-2 text-[13px]",
        lg: "min-h-[48px] rounded-xl px-5 xs:px-6 sm:px-8 py-3 text-base",
        xl: "min-h-[56px] rounded-xl px-6 xs:px-8 sm:px-10 py-3.5 text-lg",
        icon: "min-h-[44px] min-w-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
